import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import DateRangeField from '../../../../components/Modals/AlgoDatePicker';
import Loading from '../../../../components/Loading';
import ReactSelect from '../../../../components/ReactSelect';
import { COUNTRIES } from '../countries';
import { GRID_SELECT } from '../selectStyles';
import {
    COMPARISON,
    MAX_PERIOD_DAYS,
    NO_POSITION_TYPES,
    SEARCH_TYPES,
    dayBefore,
    defaultPeriod,
    formatPeriod,
    inclusiveDays,
    presetOptions,
    presetRange,
    reportingCeiling,
    retentionFloor,
    searchTypeLabel,
} from '../period';

const DEVICES = ['DESKTOP', 'MOBILE', 'TABLET'];

/**
 * The shared fields this bar can offer, and what a report gets if it says nothing.
 *
 * A report names the ones its requests actually carry. Not every GSC tool chooses a window or a
 * surface — an hourly report's window is the last 24 hours Search Console has, a URL inspection
 * has no window at all — and a control that cannot change the request must not be on screen
 * beside the ones that can.
 */
export const SCOPE_FIELDS = ['property', 'searchType', 'country', 'device', 'period', 'comparison'];

/**
 * The scope every GSC tool report is read under: which property, which surface, which window,
 * and what that window is compared against.
 *
 * Collapsed to a single bar by default: once a report is on screen its scope is something to
 * check rather than to re-enter, so the bar states the applied window and keeps the controls
 * behind a disclosure instead of pushing the figures below the fold.
 *
 * Everything inside is a draft until Apply is pressed: changing a date or a filter never fires a
 * request on its own, so the figures on screen always belong to one scope the user chose rather
 * than drifting as they tried options. Refresh repeats the applied scope unchanged.
 *
 * A report with controls of its own passes them as children — they are laid out in the same grid
 * as these, under the same Apply, so a report never grows a second place to choose its scope. It
 * can also narrow the shared set with `fields`, and replace the window the collapsed bar states
 * with `summary` — a report whose window is derived rather than chosen still belongs in this bar,
 * it just has nothing to put in the date field. A report whose request is an action rather than a
 * filter names its button with `applyLabel`, and holds it with `applyDisabled` until its own
 * controls describe a request worth sending.
 *
 * Both windows are picked with the app's shared date range field, so a period is chosen here the
 * same way it is chosen anywhere else in GDDash: the same trigger showing the applied range, the
 * same panel of presets beside a two-month calendar. The presets resolve against the property's
 * most recent day with data when the report has probed for one, so one click lands on days that
 * have something in them.
 */
class ScopeControls extends Component {
    constructor(props) {
        super(props);

        this.handlePeriod = this.handlePeriod.bind(this);
        this.handleBaselinePeriod = this.handleBaselinePeriod.bind(this);
        this.resolvePreset = this.resolvePreset.bind(this);
        this.resolveBaselinePreset = this.resolveBaselinePreset.bind(this);
    }

    /**
     * The date the presets count back from: the property's most recent day with data when the
     * report has probed for one, and otherwise the newest day a report defaults to.
     */
    anchorDate() {
        return this.props.latestDate || defaultPeriod().endDate;
    }

    resolvePreset(preset) {
        return presetRange(preset, this.anchorDate());
    }

    /**
     * The baseline's presets count back from the day before the current window opens, so "28
     * days" there means the 28 days immediately preceding it rather than the current window over
     * again — which is what a baseline anchored on the same day would have given.
     */
    resolveBaselinePreset(preset) {
        return presetRange(preset, dayBefore(this.props.draft.startDate));
    }

    /**
     * The picker hands back a half-open range while the user is still drawing one; the draft only
     * moves once both ends are set, so a report is never left holding half a window.
     */
    handlePeriod({ start_date, end_date }) {
        if (!start_date || !end_date) return;
        this.props.onChange({ startDate: start_date, endDate: end_date });
    }

    handleBaselinePeriod({ start_date, end_date }) {
        this.props.onChange({
            customBaseline: { startDate: start_date ?? '', endDate: end_date ?? '' },
        });
    }

    renderProperty() {
        const { t, draft, properties, loadingProperties, propertiesError, onChange } = this.props;

        // Each stand-in stands the same height as the select it replaces, so the row holds its
        // shape while the property list is on its way rather than settling once it lands.
        if (loadingProperties) {
            return (
                <div className="gsc-tool-field-state">
                    <Loading onlyPreloader />
                </div>
            );
        }

        if (propertiesError) {
            return <div className="gsc-tool-field-state error">{propertiesError}</div>;
        }

        if (!properties.length) {
            return (
                <div className="gsc-tool-field-state">{t('tools.gsc.controls.no_properties')}</div>
            );
        }

        const options = properties.map((property) => ({
            value: property.siteUrl,
            label: property.siteUrl,
        }));

        return (
            <ReactSelect
                options={options}
                value={options.find((option) => option.value === draft.siteUrl) ?? null}
                isSearchable={true}
                blurInputOnSelect={true}
                classname="gsc-tool-select"
                controlStyles={GRID_SELECT}
                customeOnChange={(option) => onChange({ siteUrl: option.value })}
            />
        );
    }

    render() {
        const {
            t,
            draft,
            scope,
            applied,
            onChange,
            onApply,
            onRefresh,
            onToggle,
            open,
            loading,
            loadingProperties,
            maxDays = MAX_PERIOD_DAYS,
            hideComparison,
            hidePreliminary,
            fields = SCOPE_FIELDS,
            summary,
            applyLabel,
            applyDisabled,
            children,
            footer,
        } = this.props;

        const shows = (field) => fields.includes(field);

        const countryOptions = [
            { value: '', label: t('tools.gsc.controls.all_countries') },
            ...COUNTRIES.map((country) => ({
                value: country.value,
                label: `${country.label} (${country.value})`,
            })),
        ];

        const searchTypeOptions = SEARCH_TYPES.map((value) => ({
            value,
            label: searchTypeLabel(value, t),
        }));

        const deviceOptions = [
            { value: '', label: t('tools.gsc.controls.all_devices') },
            ...DEVICES.map((device) => ({
                value: device,
                label: t(`tools.gsc.devices.${device.toLowerCase()}`),
            })),
        ];

        const comparisonOptions = Object.values(COMPARISON).map((value) => ({
            value,
            label: t(`tools.gsc.comparison.${value}`),
        }));

        // Both windows are drawn from the same calendar bounds: no earlier than Search Console
        // still answers for, and no later than today. The bound is deliberately wider than the
        // presets' anchor, which stops at the last day with final data — the days in between are
        // what "include preliminary data" asks for.
        const floor = retentionFloor();
        const ceiling = reportingCeiling();
        const presets = presetOptions(t, { maxDays });

        const currentDays = inclusiveDays({ startDate: draft.startDate, endDate: draft.endDate });
        const isCustomBaseline = draft.comparison === COMPARISON.CUSTOM;
        const shown = applied
            ? applied.current
            : { startDate: draft.startDate, endDate: draft.endDate };

        return (
            <div className="card rounded-main gsc-tool-block gsc-tool-toolbar">
                <button
                    type="button"
                    className="gsc-tool-toolbar-bar"
                    onClick={onToggle}
                    aria-expanded={open}
                >
                    {/* A report with no date field states its own window here instead, so the
                        collapsed bar always says what is on screen rather than echoing a period
                        nobody picked. */}
                    <span className="gsc-tool-toolbar-dates">
                        <i className="icon-calendar" />
                        {summary ?? (
                            <>
                                {formatPeriod(shown)}
                                <small>{t('tools.gsc.days', { days: currentDays })}</small>
                            </>
                        )}
                    </span>
                    <span className="gsc-tool-toolbar-toggle">
                        {t('tools.gsc.controls.title')}
                        <i className={open ? 'icon-arrow-up' : 'icon-arrow-down'} />
                    </span>
                </button>

                <div className={`gsc-tool-toolbar-body ${open ? '' : 'collapsed'}`}>
                    <p className="gsc-tool-toolbar-description">
                        {t('tools.gsc.controls.description')}
                    </p>

                    <div className="gsc-tool-controls">
                        {shows('property') && (
                            <div className="gsc-tool-field gsc-tool-field-wide">
                                <label>{t('tools.gsc.controls.property')}</label>
                                {this.renderProperty()}
                                {!!draft.permissionLevel && (
                                    <span className="gsc-tool-field-note">
                                        {t('tools.gsc.controls.permission', {
                                            permission: draft.permissionLevel,
                                        })}
                                    </span>
                                )}
                            </div>
                        )}

                        {shows('searchType') && (
                            <div className="gsc-tool-field">
                                <label>{t('tools.gsc.controls.search_type')}</label>
                                <ReactSelect
                                    options={searchTypeOptions}
                                    value={searchTypeOptions.find(
                                        (item) => item.value === draft.searchType,
                                    )}
                                    blurInputOnSelect={true}
                                    classname="gsc-tool-select"
                                    controlStyles={GRID_SELECT}
                                    customeOnChange={(option) =>
                                        onChange({ searchType: option.value })
                                    }
                                />
                                {NO_POSITION_TYPES.has(draft.searchType) && (
                                    <span className="gsc-tool-field-note">
                                        {t('tools.gsc.controls.search_type_no_position')}
                                    </span>
                                )}
                            </div>
                        )}

                        {shows('country') && (
                            <div className="gsc-tool-field">
                                <label>{t('tools.gsc.controls.country')}</label>
                                <ReactSelect
                                    options={countryOptions}
                                    value={countryOptions.find(
                                        (item) => item.value === draft.country,
                                    )}
                                    isSearchable={true}
                                    blurInputOnSelect={true}
                                    classname="gsc-tool-select"
                                    controlStyles={GRID_SELECT}
                                    customeOnChange={(option) =>
                                        onChange({ country: option.value })
                                    }
                                />
                            </div>
                        )}

                        {shows('device') && (
                            <div className="gsc-tool-field">
                                <label>{t('tools.gsc.controls.device')}</label>
                                <ReactSelect
                                    options={deviceOptions}
                                    value={deviceOptions.find(
                                        (item) => item.value === draft.device,
                                    )}
                                    blurInputOnSelect={true}
                                    classname="gsc-tool-select"
                                    controlStyles={GRID_SELECT}
                                    customeOnChange={(option) => onChange({ device: option.value })}
                                />
                            </div>
                        )}

                        {shows('period') && (
                            <div className="gsc-tool-field gsc-tool-field-wide">
                                <label>{t('tools.gsc.controls.current_period')}</label>
                                <div className="gsc-tool-date-field">
                                    <DateRangeField
                                        values={{
                                            start_date: draft.startDate,
                                            end_date: draft.endDate,
                                        }}
                                        updateSelect={this.handlePeriod}
                                        header={t('tools.gsc.controls.current_period')}
                                        options={presets}
                                        selectRange={this.resolvePreset}
                                        minDate={floor}
                                        maxDate={ceiling}
                                    />
                                </div>
                                <span className="gsc-tool-field-note">
                                    {t('tools.gsc.controls.period_bound', { days: maxDays })}
                                </span>
                            </div>
                        )}

                        {/* A report that reads one window leaves this out rather than offering
                            a baseline nothing on it would use. Left out rather than hidden: a
                            select that is not offered should not be in the page at all. */}
                        {shows('comparison') && !hideComparison && (
                            <div className="gsc-tool-field">
                                <label>{t('tools.gsc.controls.compare_with')}</label>
                                <ReactSelect
                                    options={comparisonOptions}
                                    value={comparisonOptions.find(
                                        (item) => item.value === draft.comparison,
                                    )}
                                    blurInputOnSelect={true}
                                    classname="gsc-tool-select"
                                    controlStyles={GRID_SELECT}
                                    customeOnChange={(option) =>
                                        onChange({ comparison: option.value })
                                    }
                                />
                                {!isCustomBaseline && (
                                    <span className="gsc-tool-field-note">
                                        {formatPeriod(scope.baseline)}
                                    </span>
                                )}
                            </div>
                        )}

                        {shows('comparison') && isCustomBaseline && !hideComparison && (
                            <div className="gsc-tool-field gsc-tool-field-wide">
                                <label>{t('tools.gsc.controls.custom_baseline')}</label>
                                <div className="gsc-tool-date-field">
                                    <DateRangeField
                                        values={{
                                            start_date: draft.customBaseline?.startDate,
                                            end_date: draft.customBaseline?.endDate,
                                        }}
                                        updateSelect={this.handleBaselinePeriod}
                                        header={t('tools.gsc.controls.custom_baseline')}
                                        options={presets}
                                        selectRange={this.resolveBaselinePreset}
                                        minDate={floor}
                                        maxDate={ceiling}
                                    />
                                </div>
                            </div>
                        )}

                        {children}
                    </div>

                    {footer}

                    <div className="gsc-tool-controls-bottom">
                        {/* The preliminary/final choice is a property of the daily data states. A
                            report on another data state — hourly, say — has nothing this checkbox
                            could switch, so it leaves it out instead of offering a toggle that
                            changes no request. The empty span keeps Apply at the right-hand end. */}
                        {hidePreliminary ? (
                            <span />
                        ) : (
                            <label className="gsc-tool-check">
                                <input
                                    type="checkbox"
                                    checked={draft.includePreliminary}
                                    onChange={(event) =>
                                        onChange({ includePreliminary: event.target.checked })
                                    }
                                />
                                <span>
                                    {t('tools.gsc.controls.include_preliminary')}
                                    <small className="d-block text-muted">
                                        {t('tools.gsc.controls.preliminary_hint')}
                                    </small>
                                </span>
                            </label>
                        )}

                        <div className="gsc-tool-actions">
                            <button
                                type="button"
                                className="button button-outline button-primary width-sm btn"
                                onClick={onRefresh}
                                disabled={loading || !applied}
                            >
                                {t('tools.gsc.controls.refresh')}
                            </button>
                            {/* There is nothing to apply until the property list is in: the
                                scope would name no property, and the request would ask Search
                                Console about nothing. */}
                            <button
                                type="button"
                                className="button button-filled button-primary width-sm btn d-flex align-items-center justify-content-center"
                                onClick={onApply}
                                disabled={
                                    loading ||
                                    loadingProperties ||
                                    !draft.siteUrl ||
                                    !!scope.errors.length ||
                                    !!applyDisabled
                                }
                            >
                                {applyLabel ?? t('tools.gsc.controls.apply')}
                                {loading && <Loading onlyPreloader />}
                            </button>
                        </div>
                    </div>

                    {scope.errors.map((error) => (
                        <div key={error} className="gsc-tool-banner error">
                            {error}
                        </div>
                    ))}
                    {scope.warnings.map((warning) => (
                        <div key={warning} className="gsc-tool-banner">
                            {warning}
                        </div>
                    ))}

                    {/* The applied line names the fields that were actually offered, plus
                        whatever the report adds. A filter the bar never showed has no business
                        appearing here as though the user had chosen it. */}
                    {!!applied && (
                        <p className="gsc-tool-applied">
                            {t('tools.gsc.controls.applied', {
                                scope: [
                                    shows('searchType') && searchTypeLabel(applied.searchType, t),
                                    shows('country') &&
                                        (applied.country
                                            ? applied.country.toUpperCase()
                                            : t('tools.gsc.controls.all_countries')),
                                    shows('device') &&
                                        (applied.device
                                            ? t(`tools.gsc.devices.${applied.device.toLowerCase()}`)
                                            : t('tools.gsc.controls.all_devices')),
                                    !hidePreliminary &&
                                        t(`tools.gsc.data_state.${applied.dataState}`),
                                    ...(this.props.extraScopeLabels ?? []),
                                ]
                                    .filter(Boolean)
                                    .join(' · '),
                            })}
                        </p>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(ScopeControls);
